import { useState } from 'react';
import { api } from '../lib/api';
import type { Prospect, Prospects as ProspectsContrat } from '../lib/contrat';
import { useRequete } from '../lib/requete';
import type { ContexteFront } from '../lib/front';
import { Badge, ErreurAffichee, Section, Squelette, Vide } from './ui';
import { BandeauFraicheur } from './Fraicheur';
import { BoiteActions } from './Actions';

/**
 * Onglet « Hors référentiel » : les sociétés avec qui SYSAXES échange et qui
 * n'ont aucune fiche Dolibarr.
 *
 * La qualification du corpus repose sur « ce domaine est-il connu de
 * Dolibarr » : un prospect jamais saisi y est donc invisible, ce qui écarte
 * précisément ce que l'on cherche. Cette liste est faite pour être vidée —
 * dès qu'une fiche est créée et le référentiel repris, la ligne disparaît.
 */
export function Prospects({ contexteFront }: { contexteFront: ContexteFront | undefined }) {
  const [categorie, setCategorie] = useState<'tous' | 'prospect_client' | 'integrateur_partenaire'>(
    'tous',
  );
  const [voirEcartees, setVoirEcartees] = useState(false);

  const { donnees, erreur, chargement, rafraichir } = useRequete<ProspectsContrat>(
    (signal, forcer) =>
      api.prospects(
        categorie === 'tous' ? undefined : categorie,
        voirEcartees,
        signal,
        forcer,
      ),
    [categorie, voirEcartees],
    true,
  );

  if (chargement) return <Squelette lignes={6} />;
  if (erreur) return <ErreurAffichee erreur={erreur} onReessayer={rafraichir} />;
  if (!donnees) return <Vide>Rien à afficher.</Vide>;

  return (
    <div className="pile">
      <BandeauFraicheur fraicheur={donnees.fraicheur} />

      <header className="entete">
        <div className="entete-tete">
          <h1>Hors référentiel</h1>
          <button type="button" className="bouton bouton--discret" onClick={rafraichir}>
            Recharger
          </button>
        </div>
        <div className="entete-meta">
          <span>
            {donnees.total} société{donnees.total > 1 ? 's' : ''}{' '}
            {voirEcartees ? 'écartée' + (donnees.total > 1 ? 's' : '') : 'sans fiche Dolibarr'}
          </span>
          {donnees.exclus > 0 && (
            <button
              type="button"
              className="lien"
              onClick={() => setVoirEcartees((v) => !v)}
            >
              {voirEcartees
                ? 'revenir à la liste'
                : `voir les ${donnees.exclus} écartée${donnees.exclus > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
        <p className="aide">
          Repérées à la lecture des objets d’e-mails. Une fois la fiche créée dans
          Dolibarr, la ligne disparaît d’elle-même à la reprise du référentiel, chaque
          nuit.
        </p>
      </header>

      <div className="filtres" role="group" aria-label="Filtrer par nature">
        {(
          [
            ['tous', 'Toutes'],
            ['prospect_client', 'Prospects et clients'],
            ['integrateur_partenaire', 'Intégrateurs'],
          ] as const
        ).map(([cle, libelle]) => (
          <button
            key={cle}
            type="button"
            className={`filtre${categorie === cle ? ' filtre--actif' : ''}`}
            aria-pressed={categorie === cle}
            onClick={() => setCategorie(cle)}
          >
            {libelle}
          </button>
        ))}
      </div>

      {donnees.total === 0 ? (
        <Vide>Aucune société hors référentiel : tout ce que nous lisons est dans Dolibarr.</Vide>
      ) : (
        <Section
          titre={voirEcartees ? 'Sociétés écartées' : 'Sociétés à créer dans Dolibarr'}
          aide="Classées par volume d’échanges."
          repliable
          cle="prospects.liste"
          action={<Badge ton="info">{donnees.total}</Badge>}
        >
          <ul className="elements">
            {donnees.elements.map((p) => (
              <LigneProspect
                key={p.domaine}
                prospect={p}
                contexteFront={contexteFront}
                onNote={rafraichir}
              />
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function LigneProspect({
  prospect,
  contexteFront,
  onNote,
}: {
  prospect: Prospect;
  contexteFront: ContexteFront | undefined;
  onNote: () => void;
}) {
  return (
    <li className="element avec-actions">
      <div>
      <div className="element-tete">
        <strong>{prospect.societe}</strong>
        <Badge
          ton={
            prospect.conversations >= 50
              ? 'critique'
              : prospect.conversations >= 10
                ? 'attention'
                : 'neutre'
          }
        >
          {prospect.conversations}
        </Badge>
      </div>

      <p className="element-meta">
        <span>{prospect.domaine}</span>
        <span className="secondaire">
          {prospect.categorie === 'integrateur_partenaire' ? 'intégrateur' : 'prospect'} ·
          dernier échange {prospect.dernier_echange ?? '—'}
          {prospect.collaborateurs > 0 &&
            ` · ${prospect.collaborateurs} collaborateur${prospect.collaborateurs > 1 ? 's' : ''}`}
        </span>
      </p>

      {prospect.motif && <p className="element-motif">{prospect.motif}</p>}

      {prospect.note && (
        <p className="element-note">
          <strong>Votre explication</strong>
          {prospect.note.auteur ? ` — ${prospect.note.auteur}` : ''} :{' '}
          {prospect.note.texte}
          {!prospect.note.prise_en_compte && (
            <span className="secondaire"> · sera relue cette nuit</span>
          )}
        </p>
      )}

      </div>

      {prospect.exclu ? (
        <div className="actions">
          <p className="actions-fait">
            Écartée{prospect.exclu.motif ? ` — ${prospect.exclu.motif}` : ''}
          </p>
          <button
            type="button"
            className="action"
            onClick={async () => {
              await api.reintegrerProspect(prospect.domaine);
              onNote();
            }}
          >
            Remettre dans la liste
          </button>
        </div>
      ) : (
        <BoiteActions
          cible={{
            domaine: prospect.domaine,
            client: prospect.societe,
            lien_front: prospect.lien_front,
          }}
          contexteFront={contexteFront}
          onFait={onNote}
        />
      )}
    </li>
  );
}


